import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { flowFetch, FlowArea, FlowBoard, FlowUser, formatDate } from './flowApi';
import { FlowTaskList } from './FlowTasks';
import { FlowDocumentList } from './FlowDocuments';

// Grade de quadros de uma área — abre o Kanban ao clicar. Admin cria quadros aqui.
function BoardsGrid({ areaId, boards, onChanged, isAdmin }: { areaId: string; boards: FlowBoard[]; onChanged: () => void; isAdmin: boolean }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedBoards, setArchivedBoards] = useState<FlowBoard[]>([]);

  const createBoard = async () => {
    if (!name.trim()) return;
    try {
      await flowFetch('/api/flow/boards', { method: 'POST', body: JSON.stringify({ areaId, name: name.trim() }) });
      toast.success('Quadro criado!');
      setName(''); setCreating(false);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao criar quadro');
    }
  };

  const loadArchived = async () => {
    try {
      const data = await flowFetch(`/api/flow/boards?areaId=${areaId}&archived=true`);
      setArchivedBoards((data.boards || []).filter((b: FlowBoard) => b.archived));
    } catch { setArchivedBoards([]); }
  };
  useEffect(() => { if (showArchived) loadArchived(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showArchived, areaId]);

  const restoreBoard = async (id: string) => {
    try {
      await flowFetch(`/api/flow/boards/${id}`, { method: 'PUT', body: JSON.stringify({ archived: false }) });
      toast.success('Quadro restaurado');
      loadArchived(); onChanged();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao restaurar'); }
  };
  const deleteBoard = async (b: FlowBoard) => {
    if (!window.confirm(`EXCLUIR o quadro "${b.name}" INTEIRO?\n\nTodas as tarefas, checklists, comentários e anexos deste quadro serão apagados permanentemente. Esta ação não pode ser desfeita.`)) return;
    try {
      const data = await flowFetch(`/api/flow/boards/${b.id}`, { method: 'DELETE' });
      toast.success(`Quadro excluído${data.deletedTasks ? ` (${data.deletedTasks} tarefa(s) removida(s))` : ''}`);
      loadArchived(); onChanged();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao excluir quadro'); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <input type="checkbox" className="accent-cyan-600" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Mostrar quadros arquivados
        </label>
      </div>
      {isAdmin && (
        <div className="mb-4 flex justify-end">
          {creating ? (
            <div className="flex gap-2">
              <input autoFocus value={name} onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createBoard(); if (e.key === 'Escape') setCreating(false); }}
                placeholder="Nome do quadro — ex.: Conteúdos Instagram"
                className="w-64 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <button onClick={createBoard} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700">Criar</button>
              <button onClick={() => setCreating(false)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600">Cancelar</button>
            </div>
          ) : (
            <button onClick={() => setCreating(true)}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cyan-700">
              + Novo quadro
            </button>
          )}
        </div>
      )}

      {boards.length === 0 ? (
        <p className="py-12 text-center text-slate-400">
          {isAdmin ? 'Nenhum quadro nesta área ainda. Crie o primeiro com "+ Novo quadro".' : 'Nenhum quadro nesta área ainda. Peça ao administrador para criar.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {boards.map(b => (
            <Link key={b.id} to={`/flow/boards/${b.id}`}
              className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-md">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-100 text-cyan-700">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v8a1 1 0 01-1 1h-4a1 1 0 01-1-1V5z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="truncate font-bold text-slate-900 group-hover:text-cyan-700">{b.name}</p>
                  <p className="text-xs text-slate-500">{b.openTasks ?? 0} tarefas abertas</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Quadros arquivados (leitura para todos; restaurar/excluir = admin) */}
      {showArchived && (
        <div className="mt-6">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Arquivados</p>
          {archivedBoards.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhum quadro arquivado nesta área.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {archivedBoards.map(b => (
                <div key={b.id} className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Link to={`/flow/boards/${b.id}`} className="truncate font-bold text-slate-900 hover:text-cyan-700">{b.name}</Link>
                        <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Arquivado</span>
                      </div>
                      {isAdmin && (
                        <div className="mt-1 flex gap-3 text-xs">
                          <button onClick={() => restoreBoard(b.id)} className="font-semibold text-emerald-600 hover:text-emerald-800">Restaurar</button>
                          <button onClick={() => deleteBoard(b)} className="font-semibold text-red-500 hover:text-red-700">Excluir</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FlowAreasList({ areas }: { areas: FlowArea[] }) {
  if (!areas.length) {
    return <p className="py-12 text-center text-slate-400">Nenhuma área disponível para você. Peça ao administrador para liberar áreas.</p>;
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {areas.map(a => (
        <Link key={a.id} to={`/flow/areas/${a.id}`}
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl text-lg font-black text-white" style={{ background: a.color }}>
              {a.name.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="truncate font-bold text-slate-900">{a.name}</p>
              <p className="text-xs text-slate-500">{a.openTasks ?? 0} tarefas abertas</p>
            </div>
          </div>
          {a.description && <p className="mt-3 line-clamp-2 text-sm text-slate-500">{a.description}</p>}
        </Link>
      ))}
    </div>
  );
}

type AreaTab = 'quadros' | 'resumo' | 'tarefas' | 'documentos' | 'atividades';

export function FlowAreaDetail({ areaId, areas, users, isAdmin }: { areaId: string; areas: FlowArea[]; users: FlowUser[]; isAdmin: boolean }) {
  const [tab, setTab] = useState<AreaTab>('quadros');
  const [data, setData] = useState<any>(null);

  const load = async () => {
    try {
      setData(await flowFetch(`/api/flow/areas/${areaId}/summary`));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar área');
    }
  };
  useEffect(() => { setData(null); load(); }, [areaId]);

  if (!data) return <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>;

  const area = data.area;
  const tabBtn = (t: AreaTab, label: string) => (
    <button key={t} onClick={() => setTab(t)}
      className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${tab === t ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}>
      {label}
    </button>
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Link to="/flow/areas" className="text-sm text-slate-400 hover:text-slate-600">← Áreas</Link>
        <span className="flex h-9 w-9 items-center justify-center rounded-xl text-base font-black text-white" style={{ background: area.color }}>
          {area.name.charAt(0).toUpperCase()}
        </span>
        <div>
          <h2 className="text-lg font-bold text-slate-900">{area.name}</h2>
          {area.description && <p className="text-xs text-slate-500">{area.description}</p>}
        </div>
      </div>

      <div className="mb-4 inline-flex gap-1 rounded-2xl bg-slate-100 p-1">
        {tabBtn('quadros', 'Quadros')}
        {tabBtn('resumo', 'Resumo')}
        {tabBtn('tarefas', 'Tarefas')}
        {tabBtn('documentos', 'Documentos')}
        {tabBtn('atividades', 'Atividades')}
      </div>

      {tab === 'quadros' && (
        <BoardsGrid areaId={areaId} boards={data.boards || []} onChanged={load} isAdmin={isAdmin} />
      )}

      {tab === 'resumo' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { label: 'Tarefas abertas', value: data.summary.open, cls: 'text-slate-900' },
              { label: 'Atrasadas', value: data.summary.overdue, cls: data.summary.overdue ? 'text-red-600' : 'text-slate-900' },
              { label: 'Aguardando', value: data.summary.waiting, cls: 'text-amber-600' },
              { label: 'Concluídas (7 dias)', value: data.summary.doneRecent, cls: 'text-emerald-600' },
            ].map((c, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                <p className={`text-2xl font-black ${c.cls}`}>{c.value}</p>
                <p className="mt-1 text-xs font-semibold text-slate-500">{c.label}</p>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Últimos comentários</p>
            {data.latestComments.length === 0 ? <p className="text-sm text-slate-400">Nenhum comentário recente.</p> : (
              <ul className="space-y-2">
                {data.latestComments.map((c: any) => (
                  <li key={c.id} className="text-sm text-slate-700">
                    <span className="font-semibold">{c.author?.nome || '—'}:</span> {c.content}
                    <span className="ml-1 text-xs text-slate-400">· {formatDate(c.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'tarefas' && <FlowTaskList areas={areas} users={users} fixedAreaId={areaId} />}
      {tab === 'documentos' && <FlowDocumentList areas={areas} users={users} fixedAreaId={areaId} />}

      {tab === 'atividades' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {data.activities.length === 0 ? <p className="text-sm text-slate-400">Nenhuma atividade registrada.</p> : (
            <ul className="space-y-2">
              {data.activities.map((a: any) => (
                <li key={a.id} className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">{a.user?.nome || '—'}</span> {a.message}
                  <span className="ml-1 text-xs text-slate-400">· {formatDate(a.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
